import { Link } from "react-router-dom";
import { ArrowUpRight, Plane } from "lucide-react";
import MaskedReveal from "@/components/MaskedReveal";
import FadeUp from "@/components/FadeUp";
import Breadcrumbs from "@/components/Breadcrumbs";
import SEO from "@/components/SEO";
import { NRI_HUB, COUNTRIES } from "@/data/nri";
import { WHATSAPP_LINK } from "@/lib/api";

export default function NRI() {
  return (
    <div data-testid="nri-hub-page" className="pt-28 md:pt-36 pb-28">
      <SEO
        title="For NRI Patients — Vascular & Endovascular IR at Max Mohali · Dr Tegbir Vascular"
        description="Video-first NRI vascular care for patients travelling from Canada, USA, UK, Australia, UAE and Saudi Arabia. Continuity from your home city, to Mohali, and back."
        keywords={["NRI vascular treatment mohali", "medical visa india", "punjab medical tourism", "vascular embolisation india NRI"]}
      />
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <FadeUp>
          <Breadcrumbs trail={[{ to: "/", label: "Home" }, { label: "For NRI" }]} />
          <div className="mt-8 kbd">— {NRI_HUB.kicker}</div>
        </FadeUp>

        <div className="grid grid-cols-12 gap-8 mt-12">
          <div className="col-span-12 md:col-span-8">
            <h1 className="font-serif text-6xl md:text-[7.5vw] leading-[0.94] tracking-tightest">
              <MaskedReveal text={"Continuity"}/>
              <MaskedReveal text={"across"} delay={0.1}/>
              <MaskedReveal text={"borders."} delay={0.2} className="italic font-normal"/>
            </h1>
            <FadeUp className="mt-10">
              <p className="text-xl md:text-2xl font-serif italic text-ink max-w-3xl leading-snug">"{NRI_HUB.lede}"</p>
              <p className="mt-8 text-lg text-ink-muted leading-relaxed max-w-2xl">{NRI_HUB.intro}</p>
            </FadeUp>
          </div>

          <FadeUp className="col-span-12 md:col-span-4 md:pl-8" delay={0.2}>
            <div className="hairline bg-white/70 p-6">
              <div className="kbd">Fastest first step</div>
              <p className="mt-3 text-sm text-ink-muted leading-relaxed">
                WhatsApp with a one-line summary of your case and any existing imaging. The coordinator opens a thread with Dr Sidhu.
              </p>
              <a target="_blank" rel="noreferrer" href={WHATSAPP_LINK("Hello Dr Sidhu, I'm writing from abroad and would like to enquire about a consultation.")} data-testid="nri-hub-whatsapp" className="mt-6 btn-pill-inv w-full justify-center">
                Start on WhatsApp <ArrowUpRight size={14}/>
              </a>
              <Link to="/contact" data-testid="nri-hub-form" className="mt-3 btn-pill w-full justify-center">
                Or send the form
              </Link>
            </div>
          </FadeUp>
        </div>

        {/* Promises */}
        <div className="mt-32">
          <FadeUp>
            <div className="kbd">— How the practice handles distance</div>
            <h2 className="mt-4 font-serif text-4xl md:text-6xl tracking-tightest leading-[1.05] max-w-3xl">
              Six promises the practice keeps every time.
            </h2>
          </FadeUp>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
            {NRI_HUB.promises.map((p, i) => (
              <FadeUp key={p.k} delay={0.05 * i}>
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-2 font-mono text-crimson text-lg">0{i + 1}</div>
                  <div className="col-span-10">
                    <h3 className="font-serif text-2xl md:text-3xl tracking-tightest">{p.k}</h3>
                    <p className="mt-3 text-ink-muted leading-relaxed">{p.v}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>

        {/* Country grid */}
        <div className="mt-32 md:mt-40">
          <FadeUp>
            <div className="grid grid-cols-12 gap-6 items-end mb-16">
              <div className="col-span-12 md:col-span-7">
                <div className="kbd">— Six countries. One practice.</div>
                <h2 className="mt-4 font-serif text-4xl md:text-6xl tracking-tightest leading-[1.05]">Choose your country</h2>
              </div>
              <div className="col-span-12 md:col-span-5 md:text-right kbd text-ink-muted">Every country is different. So is the page.</div>
            </div>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10 hairline">
            {COUNTRIES.map((c, i) => (
              <FadeUp key={c.slug} delay={i * 0.04} className="bg-bone">
                <Link
                  to={`/nri/${c.slug}`}
                  data-testid={`country-card-${c.slug}`}
                  className="group block p-6 md:p-8 h-full hover:bg-white transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <span className="text-4xl leading-none" aria-hidden>{c.flag}</span>
                    <ArrowUpRight size={18} className="text-ink-muted group-hover:text-crimson group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform"/>
                  </div>
                  <h3 className="mt-6 font-serif text-3xl md:text-4xl tracking-tightest group-hover:text-crimson transition-colors">{c.name}</h3>
                  <p className="mt-3 text-sm text-ink-muted leading-relaxed max-w-md">{c.lede}</p>
                  <div className="mt-5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-ink-muted">
                    {c.cities.slice(0, 3).map((city) => <span key={city} className="border-b border-ink/10">{city}</span>)}
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>

        {/* Closing */}
        <FadeUp className="mt-32">
          <div className="hairline-t pt-12 grid grid-cols-12 gap-6">
            <div className="col-span-12 md:col-span-8">
              <h3 className="font-serif text-4xl md:text-6xl tracking-tightest leading-[1.02] max-w-3xl">
                <MaskedReveal text={"Distance"}/>
                <MaskedReveal text={"stops being"} delay={0.1}/>
                <MaskedReveal text={"a barrier."} delay={0.2} className="italic font-normal"/>
              </h3>
            </div>
            <div className="col-span-12 md:col-span-4 md:pl-8 self-end">
              <div className="flex flex-wrap gap-3">
                <Link to="/contact" className="btn-pill-inv">Book NRI consult <ArrowUpRight size={14}/></Link>
                <Link to="/how-it-works" className="btn-pill">The 6-step process</Link>
              </div>
            </div>
          </div>
        </FadeUp>
      </div>
    </div>
  );
}
